import { createI18n } from 'vue-i18n'
import { STORAGE_KEYS } from '@/utils/storageKeys'

// Importar traduções
import ptBR from '@/locales/pt-BR.json'
import enUS from '@/locales/en-US.json'
import es from '@/locales/es.json'

/** Locales suportados pelo painel (alinhado ao wizard de instalação e ao backend) */
export const INSTALL_LOCALES = ['pt-BR', 'en-US', 'es']

/**
 * Detectar idioma do navegador
 */
function getBrowserLocale() {
  const navigatorLocale = navigator.language || navigator.userLanguage
  
  // Mapear de 'pt-BR' ou 'pt' para 'pt-BR'
  if (navigatorLocale.startsWith('pt')) return 'pt-BR'
  if (navigatorLocale.startsWith('en')) return 'en-US'
  if (navigatorLocale.startsWith('es')) return 'es'
  
  return 'en-US' // fallback internacional
}

/**
 * Obter idioma com prioridade:
 * 1. Preferência salva do usuário (localStorage)
 * 2. Idioma padrão gravado na instalação (`config.php` → GET /api/install/check)
 * 3. Idioma padrão do sistema (localStorage, ex. admin)
 * 4. Idioma do navegador
 */
function getInitialLocale(serverDefaultFromConfig = null) {
  // 1. Preferência do usuário
  const userLocale = localStorage.getItem(STORAGE_KEYS.LOCALE)
  if (userLocale) {

    return userLocale
  }

  // 2. Instalação (servidor)
  if (
    serverDefaultFromConfig &&
    INSTALL_LOCALES.includes(serverDefaultFromConfig)
  ) {

    return serverDefaultFromConfig
  }

  // 3. Idioma padrão do sistema (localStorage)
  const systemLocale = localStorage.getItem(STORAGE_KEYS.SYSTEM_LOCALE)
  if (systemLocale) {

    return systemLocale
  }

  // 4. Idioma do navegador
  const browserLocale = getBrowserLocale()

  return browserLocale
}

/** GET /api/install/check — default_locale só quando ainda não há preferência explícita do utilizador */
export async function fetchServerDefaultLocale() {
  if (typeof window === 'undefined' || localStorage.getItem(STORAGE_KEYS.LOCALE)) {
    return null
  }
  try {
    const raw = import.meta.env.BASE_URL || '/'
    const prefix =
      raw === '/' || raw === './' ? '' : String(raw).replace(/\/$/, '')
    const path = prefix ? `${prefix}/api/install/check` : '/api/install/check'
    const res = await fetch(path, { headers: { Accept: 'application/json' } })
    if (!res.ok) return null
    const data = await res.json()
    const loc = data?.default_locale
    if (typeof loc === 'string' && INSTALL_LOCALES.includes(loc)) {
      return loc
    }
  } catch {
    /* rede / CORS / painel ainda sem API */
  }
  return null
}

let i18nSingleton = null

export function getI18n() {
  if (!i18nSingleton) {
    throw new Error('[i18n] createPanelI18n() must run before getI18n()')
  }
  return i18nSingleton
}

export function createPanelI18n(serverDefaultFromConfig = null) {
  i18nSingleton = createI18n({
  legacy: false, // Usar Composition API
  locale: getInitialLocale(serverDefaultFromConfig),
  fallbackLocale: ['pt-BR', 'pt', 'en-US'], // Fallback em cascata
  globalInjection: true, // Permitir usar $t globalmente
  missingWarn: false, // Desabilitar warnings de chaves faltando em dev
  fallbackWarn: false, // Desabilitar warnings de fallback em dev
  messages: {
    'pt-BR': ptBR,
    'pt': ptBR, // Alias para pt-BR (fallback para navegadores que enviam apenas 'pt')
    'en-US': enUS,
    'en': enUS, // Alias para en-US
    'es': es
  },
  
  // Configurações de formatação de data
  datetimeFormats: {
    'pt-BR': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        weekday: 'long'
      },
      datetime: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }
    },
    'en-US': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        weekday: 'long'
      },
      datetime: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }
    },
    'es': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        weekday: 'long'
      },
      datetime: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }
    }
  },
  
  // Configurações de formatação de número
  numberFormats: {
    'pt-BR': {
      currency: {
        style: 'currency',
        currency: 'BRL',
        currencyDisplay: 'symbol'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        minimumFractionDigits: 1
      }
    },
    'en-US': {
      currency: {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'symbol'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        minimumFractionDigits: 1
      }
    },
    'es': {
      currency: {
        style: 'currency',
        currency: 'EUR',
        currencyDisplay: 'symbol'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        minimumFractionDigits: 1
      }
    }
  }
  })

  document.querySelector('html')?.setAttribute('lang', i18nSingleton.global.locale.value)
  return i18nSingleton
}

// Função helper para trocar idioma
export function changeLocale(newLocale, saveAsUserPreference = true) {
  if (!i18nSingleton) return
  i18nSingleton.global.locale.value = newLocale
  
  if (saveAsUserPreference) {
    // Salvar como preferência do usuário
    localStorage.setItem(STORAGE_KEYS.LOCALE, newLocale)

  } else {
    // Salvar como idioma padrão do sistema
    localStorage.setItem(STORAGE_KEYS.SYSTEM_LOCALE, newLocale)

  }
  
  // Atualizar HTML lang attribute
  document.querySelector('html')?.setAttribute('lang', newLocale)
}

// Função para obter idiomas disponíveis
export function getAvailableLocales() {
  return {
    'pt-BR': {
      code: 'pt-BR',
      name: 'Português (Brasil)',
      native: 'Português',
      flag: '🇧🇷',
      currency: 'BRL',
      currencySymbol: 'R$'
    },
    'en-US': {
      code: 'en-US',
      name: 'English (United States)',
      native: 'English',
      flag: '🇺🇸',
      currency: 'USD',
      currencySymbol: '$'
    },
    'es': {
      code: 'es',
      name: 'Español',
      native: 'Español',
      flag: '🇪🇸',
      currency: 'EUR',
      currencySymbol: '€'
    }
  }
}

// Definir HTML lang no carregamento inicial (feito em createPanelI18n)

